import { Button, IconButton, SelectInput, SelectInputItem, TextField } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EClassModuleRecurrenceType } from '@/shared/api/generated';

import type { ClassModuleRecurrenceForm } from '../../../types/classModuleForm';

export interface RecurrenceModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onAdd: (recurrence: ClassModuleRecurrenceForm) => void;
}

const WEEK_DAYS = [0, 1, 2, 3, 4, 5, 6] as const;

/**
 * Modal empilhado de recorrência. Coleta tipo (semanal/mensal), intervalo, dia
 * (semana ou mês, conforme o tipo) e horários, retornando uma
 * `ClassModuleRecurrenceForm` ao confirmar.
 */
export function RecurrenceModal({ open, onOpenChange, onAdd }: RecurrenceModalProps) {
  const { t } = useTranslation('classModuleManagement');
  const { t: tCommon } = useTranslation('common');

  const [type, setType] = useState<EClassModuleRecurrenceType | null>(null);
  const [interval, setInterval] = useState('1');
  const [weekDay, setWeekDay] = useState('2');
  const [monthDay, setMonthDay] = useState('5');
  const [startTime, setStartTime] = useState('20:00');
  const [endTime, setEndTime] = useState('21:00');
  const [showError, setShowError] = useState(false);

  const isMonthly = type === 2;
  const timeOrderInvalid = startTime !== '' && endTime !== '' && startTime >= endTime;

  const reset = () => {
    setType(null);
    setInterval('1');
    setWeekDay('2');
    setMonthDay('5');
    setStartTime('20:00');
    setEndTime('21:00');
    setShowError(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleAdd = () => {
    if (type === null || timeOrderInvalid) {
      setShowError(true);
      return;
    }
    onAdd({
      type,
      interval: Number(interval) || 1,
      weekDay: type === 1 ? Number(weekDay) : null,
      monthDay: type === 2 ? Number(monthDay) : null,
      startTime,
      endTime,
    });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <Dialog.Portal>
        {/* Backdrop manual: com `modal={false}` o Radix não renderiza o Dialog.Overlay
            nem aplica `pointer-events: none` no body — necessário para que os dropdowns
            do SelectInput (portados para o body) recebam cliques. */}
        <div
          aria-hidden="true"
          className="fixed inset-none z-[60] h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft"
        />
        <Dialog.Content
          aria-describedby={undefined}
          // Não fechar por interação externa: cliques nos dropdowns (portados para o
          // body) contam como "fora" e disparariam o dismiss.
          onInteractOutside={e => e.preventDefault()}
          className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-[60] flex max-h-[90vh] w-full max-w-[35rem] flex-col rounded-md bg-ctx-layout-surface shadow-level4 focus:outline-none"
        >
          <div className="flex items-center justify-between border-ctx-layout-border border-b-hairline px-xxs py-xxxs">
            <Dialog.Title className="text-ctx-content-title ts-heading-xs">
              {t('recurrenceModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={tCommon('actions.cancel')}
              />
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-xxs py-xxs">
            <div className="grid grid-cols-1 gap-mini sm:grid-cols-2">
              <SelectInput
                label={`${t('recurrenceModal.repeat')}*`}
                placeholder={t('recurrenceModal.repeatPlaceholder')}
                value={type !== null ? String(type) : ''}
                onValueChange={v => {
                  setType(v ? (Number(v) as EClassModuleRecurrenceType) : null);
                  setShowError(false);
                }}
                size="md"
                fullWidth
                hasError={showError && type === null}
                {...(showError && type === null ? { helper: t('recurrenceModal.required') } : {})}
              >
                <SelectInputItem value="1" custom={{ root: 'text-nowrap' }}>
                  {t('recurrenceModal.repeatOptions.weekly')}
                </SelectInputItem>
                <SelectInputItem value="2" custom={{ root: 'text-nowrap' }}>
                  {t('recurrenceModal.repeatOptions.monthly')}
                </SelectInputItem>
              </SelectInput>

              <TextField
                label={t('recurrenceModal.interval')}
                type="number"
                value={interval}
                onChange={e => setInterval(e.target.value)}
                size="md"
                fullWidth
                optionalMessage={
                  isMonthly ? t('recurrenceModal.unit.month') : t('recurrenceModal.unit.week')
                }
              />

              {isMonthly ? (
                <div className="sm:col-span-2">
                  <TextField
                    label={t('recurrenceModal.monthDay')}
                    type="number"
                    fullWidth
                    value={monthDay}
                    onChange={e => setMonthDay(e.target.value)}
                    size="md"
                  />
                </div>
              ) : (
                <div className="sm:col-span-2">
                  <SelectInput
                    label={t('recurrenceModal.weekDay')}
                    value={weekDay}
                    onValueChange={setWeekDay}
                    size="md"
                    fullWidth
                  >
                    {WEEK_DAYS.map(day => (
                      <SelectInputItem
                        key={day}
                        value={String(day)}
                        custom={{ root: 'text-nowrap' }}
                      >
                        {t(`recurrenceModal.weekDays.${day}` as 'recurrenceModal.weekDays.0')}
                      </SelectInputItem>
                    ))}
                  </SelectInput>
                </div>
              )}

              <TextField
                label={t('recurrenceModal.startTime')}
                type="time"
                fullWidth
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                size="md"
                hasError={showError && timeOrderInvalid}
                {...(showError && timeOrderInvalid
                  ? { helper: t('recurrenceModal.timeOrderError') }
                  : {})}
              />
              <TextField
                label={t('recurrenceModal.endTime')}
                type="time"
                fullWidth
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                size="md"
                hasError={showError && timeOrderInvalid}
              />
            </div>
          </div>

          <div className="flex justify-end gap-micro border-ctx-layout-border border-t-hairline px-xxs py-xxxs">
            <Dialog.Close asChild>
              <Button hierarchy="secondary" size="md">
                {t('recurrenceModal.clear')}
              </Button>
            </Dialog.Close>
            <Button hierarchy="primary" size="md" onClick={handleAdd}>
              {t('recurrenceModal.add')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
